import { useEffect, useMemo, useState } from "react";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { listAssessmentScores } from "../graphql/queries.js";
import "./ExploreHealth.css";

const client = generateClient();
const dimensions = [
  {
    key: "MHScore",
    label: "Mental Health",
    max: 66,
    idealMax: 16,
    watchMax: 32,
    color: "#2878b8",
    actions: [
      {
        text: "Set aside five minutes each day for a calming reset, such as breathing, journaling, or a short walk.",
        why: "A brief daily reset builds a steady habit you can rely on before stress builds up.",
      },
      {
        text: "Choose one trusted person to check in with this week.",
        why: "Talking with someone you trust can make stress feel more manageable and less isolating.",
      },
    ],
  },
  {
    key: "PHScore",
    label: "Physical Health",
    max: 30,
    idealMax: 5,
    watchMax: 10,
    color: "#2f7d69",
    actions: [
      {
        text: "Take a 10-minute outdoor walk on three days this week.",
        why: "Short, regular walks are easier to sustain than long workouts and still support your activity level.",
      },
      {
        text: "Add water or a fruit or vegetable to one daily routine.",
        why: "Attaching it to a routine you already have makes the change easier to remember.",
      },
    ],
  },
  {
    key: "SHScore",
    label: "Spiritual Health",
    max: 28,
    idealMax: 7,
    watchMax: 14,
    color: "#bb7e28",
    actions: [
      {
        text: "Make space for one personally meaningful reflective practice this week.",
        why: "A consistent time and place makes the practice easier to return to.",
      },
      {
        text: "Reconnect with a source of meaning, community, or gratitude.",
        why: "Reconnecting regularly helps sustain a sense of purpose and belonging.",
      },
    ],
  },
  {
    key: "SoHScore",
    label: "Social Health",
    max: 48,
    idealMax: 12,
    watchMax: 24,
    color: "#9c4a6b",
    actions: [
      {
        text: "Plan one small, intentional connection with someone supportive.",
        why: "Planning it ahead makes it more likely to actually happen.",
      },
      {
        text: "Practice one boundary or ask for support in a specific situation.",
        why: "Practicing in one specific situation builds confidence before applying it more broadly.",
      },
    ],
  },
  {
    key: "CMScore",
    label: "Coping Mechanisms",
    max: 48,
    idealMax: 1,
    watchMax: 3,
    color: "#6d5a9d",
    actions: [
      {
        text: "Choose one practical coping strategy and use it at the same time each day.",
        why: "Practicing the strategy daily helps with memory recall when you need it most and is proactive.",
      },
      {
        text: "Notice one difficult moment and write down the coping response you tried.",
        why: "Writing it down helps you notice what works so you can repeat it next time.",
      },
    ],
  },
];

const questionPrompts = [
  {
    id: "combination",
    text: "What combination of physical, mental, spiritual, social, and coping characteristics describes me?",
  },
  {
    id: "strengths-opportunities",
    text: "What are my strongest areas and greatest opportunities?",
  },
  {
    id: "best-habit",
    text: "Which behavior or habit is likely to be most useful for me?",
  },
  {
    id: "similar-people",
    text: "Among people with similar mental-health scores, which behaviors are associated with better physical-health outcomes?",
  },
  {
    id: "consistent-improvement",
    text: "Which factors appear most consistently associated with improvement between assessments?",
  },
  { id: "what-changed", text: "What changed since my previous assessment?" },
  { id: "profile-summary", text: "What is a summary of my profile?" },
  {
    id: "next-step",
    text: "What is likely the most beneficial next step?",
  },
];

const responseThemes = {
  PHScore: "nutrition, water, activity, and sweets",
  MHScore: "sleep, anxiety, stress, loneliness, and worry",
  SHScore: "personal beliefs, spiritual practice, and community connection",
  SoHScore: "stability, safety, relationships, boundaries, and contribution",
  CMScore: "movement, writing, counseling, and positive coping practices",
};
// Individual assessment questions behind each domain score, sourced from Assessment.js field labels.
const questionCatalog = {
  PHScore: [
    { key: "PHVegeFruits", label: "Vegetables and/or fruit" },
    { key: "PHBeanLentils", label: "Beans and/or lentils" },
    { key: "PHGrainBreads", label: "Grains and/or breads" },
    { key: "PHDairy", label: "Dairy" },
    { key: "PHMeat", label: "Animal meat" },
    { key: "PHFishSeafood", label: "Fish/seafood" },
    { key: "PHSweets", label: "Sweets/desserts" },
    { key: "PHWater", label: "Water intake" },
    { key: "PHPhysicalActivity", label: "Physical activity" },
  ],
  MHScore: [
    { key: "MHSleeplessness", label: "Sleeplessness" },
    { key: "MHAnxiety", label: "Fight, flight, or freeze response" },
    { key: "MHDepression", label: "Depression" },
    { key: "MHGrief", label: "Grief" },
    { key: "MHGuilt", label: "Guilt" },
    { key: "MHIrritability", label: "Irritability" },
    { key: "MHStress", label: "Stress" },
    { key: "MHRegret", label: "Regret" },
    { key: "MHLoneliness", label: "Loneliness" },
    { key: "MHWorry", label: "Worry / anxiety" },
    { key: "MHHope", label: "Hopelessness" },
    { key: "MHPeace", label: "Inner turmoil" },
    { key: "MHJoy", label: "Emptiness" },
  ],
  SHScore: [
    { key: "SHSpiritualDefine", label: "Defining personal spirituality" },
    { key: "SHSpiritualIntegrate", label: "Integrating spiritual beliefs" },
    { key: "SHPrayer", label: "Prayer" },
    { key: "SHSpiritualActivity", label: "Spiritual activity" },
    { key: "SHReadText", label: "Reading religious text" },
    { key: "SHAlignText", label: "Living in alignment with beliefs" },
    { key: "SHCommunity", label: "Spiritual community connection" },
  ],
  SoHScore: [
    { key: "SoHLearning", label: "Willingness to learn and be corrected" },
    { key: "SoHStable", label: "Stability" },
    { key: "SoHSafety", label: "Safety and security" },
    { key: "SoHKindness", label: "Kindness toward others" },
    { key: "SoHForgiveness", label: "Forgiveness" },
    { key: "SoHPatience", label: "Patience" },
    { key: "SoHRelationships", label: "Supported relationships" },
    { key: "SoHContribution", label: "Contribution to others" },
    { key: "SoHBoundaries", label: "Healthy boundaries" },
    { key: "SoHEUnpleasant", label: "Managing unpleasant emotions" },
    { key: "SoHEPleasant", label: "Experiencing pleasant emotions" },
    { key: "SoHEControl", label: "Control over emotions" },
  ],
  CMScore: [
    { key: "CMAlcohol", label: "Alcohol use", direction: "negative" },
    { key: "CMDrugs", label: "Drug use", direction: "negative" },
    { key: "CMOvereating", label: "Overeating", direction: "negative" },
    { key: "CMSmoking", label: "Smoking", direction: "negative" },
    { key: "CMGaming", label: "Gaming", direction: "negative" },
    { key: "CMShopping", label: "Shopping", direction: "negative" },
    { key: "CMGambling", label: "Gambling", direction: "negative" },
    { key: "CMSex", label: "Casual sex", direction: "negative" },
    { key: "CMPorn", label: "Pornography", direction: "negative" },
    {
      key: "CMBlaming",
      label: "Blaming self or others",
      direction: "negative",
    },
    {
      key: "CMHurting",
      label: "Hurting self or others",
      direction: "negative",
    },
    {
      key: "CMDisengage",
      label: "Disengagement/isolation",
      direction: "negative",
    },
    { key: "CMArt", label: "Art", direction: "positive" },
    { key: "CMMusic", label: "Music", direction: "positive" },
    { key: "CMPoetry", label: "Poetry", direction: "positive" },
    { key: "CMReading", label: "Reading", direction: "positive" },
    { key: "CMGroups", label: "Group discussion", direction: "positive" },
    {
      key: "CMCounseling",
      label: "Individual counseling",
      direction: "positive",
    },
    { key: "CMVenting", label: "Talking/venting", direction: "positive" },
    { key: "CMWriting", label: "Writing/journaling", direction: "positive" },
    { key: "CMSensory", label: "Sensory/fidgets", direction: "positive" },
    { key: "CMDancing", label: "Dancing", direction: "positive" },
    { key: "CMExercising", label: "Exercising", direction: "positive" },
    { key: "CMWalking", label: "Walking", direction: "positive" },
    {
      key: "CMChange",
      label: "Changing approach for different results",
      direction: "positive",
    },
    {
      key: "CMAnalyze",
      label: "Analyzing the situation",
      direction: "positive",
    },
    {
      key: "CMDaydream",
      label: "Daydreaming/imagining a better situation",
      direction: "positive",
    },
    {
      key: "CMPositive",
      label: "Focusing on the positives",
      direction: "positive",
    },
  ],
};
// A question's stored score is 0 when the response is already favorable; higher means less ideal.
function contributingQuestions(
  dimensionKey,
  record,
  { limit = 2, favorable = false } = {},
) {
  const items = (questionCatalog[dimensionKey] || []).map((item) => ({
    ...item,
    value: Number(record?.[item.key]) || 0,
  }));
  const filtered = favorable
    ? items.filter((item) => item.value === 0)
    : items.filter((item) => item.value > 0).sort((a, b) => b.value - a.value);
  return filtered.slice(0, limit);
}
// Positive coping items are scored inversely (0 = most frequent), so invert them onto a shared 0-4 frequency scale.
function rankCopingByFrequency(record, limit = 3) {
  return (questionCatalog.CMScore || [])
    .map((item) => ({ ...item, value: Number(record?.[item.key]) || 0 }))
    .map((item) => ({
      ...item,
      frequencyRank:
        item.direction === "negative" ? item.value : (0.04 - item.value) * 100,
    }))
    .filter((item) => item.frequencyRank > 0)
    .sort((a, b) => b.frequencyRank - a.frequencyRank)
    .slice(0, limit);
}
const asQuestion = (dimension, item) => ({
  key: item.key,
  label: item.label,
  dimension: dimension.label,
  status: item.value > 0 ? "Elevated response" : "Favorable response",
});
const scoreFor = (record, dimension) => Number(record?.[dimension.key]) || 0;
// CompletedDate is set once at finalization and never changes; updatedAt can drift from later edits (e.g. professional review).
const assessmentDateFor = (record) =>
  new Date(
    record?.CompletedDate || record?.updatedAt || record?.createdAt || 0,
  );
const isAtLeastOneMonthApart = (laterDate, earlierDate) => {
  const cutoff = new Date(laterDate);
  cutoff.setMonth(cutoff.getMonth() - 1);
  return earlierDate <= cutoff;
};
const isAtLeastOneMonthEarlier = (latestRecord, candidateRecord) =>
  isAtLeastOneMonthApart(
    assessmentDateFor(latestRecord),
    assessmentDateFor(candidateRecord),
  );
const formatAssessmentDate = (record) =>
  assessmentDateFor(record).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
const levelFor = (score, dimension) => {
  if (score <= dimension.idealMax) return "ideal range";
  if (score <= dimension.watchMax) return "room to improve";
  return "area to improve";
};
const titleCase = (value) =>
  value.replace(/\b\w/g, (letter) => letter.toUpperCase());

function domainSummary(dimension, previous) {
  const status = levelFor(dimension.score, dimension);
  const changeNote = previous
    ? dimension.change < 0
      ? " Your score is more favorable than at your previous assessment."
      : dimension.change > 0
        ? " Your score is less favorable than at your previous assessment."
        : " Your score is unchanged since your previous assessment."
    : " A future assessment can help show change over time.";

  if (status === "ideal range") {
    return `Your ${dimension.label.toLowerCase()} score is within the ideal range.${changeNote}`;
  }
  return `Your ${dimension.label.toLowerCase()} score indicates ${status}.${changeNote}`;
}

// Keywords used to route free-text questions to the domain(s) they are actually about.
const domainKeywords = {
  CMScore: ["coping", "cope"],
  PHScore: ["physical", "nutrition", "sweets", "exercise"],
  MHScore: ["mental", "anxiety", "stress", "sleep"],
  SHScore: ["spiritual", "faith", "prayer"],
  SoHScore: ["social", "relationship", "boundar"],
};

function classifyQuestion(text) {
  const normalized = text.toLowerCase();
  if (normalized.includes("trauma")) return "unsupported-trauma";
  if (normalized.includes("changed")) return "what-changed";
  if (
    normalized.includes("population") ||
    normalized.includes("people with") ||
    (normalized.includes("similar") && normalized.includes("people"))
  )
    return "similar-people";
  if (normalized.includes("consistent") || normalized.includes("factor"))
    return "consistent-improvement";
  if (normalized.includes("combination") || normalized.includes("describes me"))
    return "combination";
  if (normalized.includes("strong") || normalized.includes("opportunit"))
    return "strengths-opportunities";
  if (normalized.includes("habit") || normalized.includes("behavior"))
    return "best-habit";
  if (normalized.includes("next step") || normalized.includes("beneficial"))
    return "next-step";

  // A question naming two of the user's own domains asks about an association within their
  // own results (e.g. "are my top coping responses associated with mental health"), not a
  // population comparison.
  const matchedDimensions = Object.entries(domainKeywords)
    .filter(([, words]) => words.some((word) => normalized.includes(word)))
    .map(([key]) => key);
  if (matchedDimensions.length >= 2)
    return `link-${matchedDimensions[0]}_${matchedDimensions[1]}`;
  if (matchedDimensions.length === 1) return `domain-${matchedDimensions[0]}`;

  return "profile-summary";
}

// Insights are derived only from computed profile/question values, never invented from raw scores.
function buildInsight(id, ctx) {
  const { profile, previous, latest, history, comparisonInsights } = ctx;

  if (id === "unsupported-trauma") {
    return {
      text: "This explorer does not have an answer for that. It only analyzes your Physical, Mental, Spiritual, Social, and Coping Mechanisms responses from your latest assessment, and does not assess trauma history or its relationship to other domains.",
      questions: [],
      primary: [],
      actionable: null,
      alternates: null,
      followUps: ["profile-summary"],
    };
  }

  const strongest = [...profile].sort((a, b) => a.ratio - b.ratio)[0];
  // A domain average can look ideal even when one specific response within it is elevated,
  // so opportunities are detected at the individual-question level first.
  const rankedQuestions = profile
    .flatMap((dimension) =>
      contributingQuestions(dimension.key, latest, { limit: 5 }).map(
        (item) => ({ ...item, dimension }),
      ),
    )
    .sort((a, b) => b.value - a.value);
  const topQuestion = rankedQuestions[0] || null;
  const hasOpportunity = Boolean(topQuestion);
  const opportunityDimension = topQuestion ? topQuestion.dimension : strongest;
  const opportunityQuestions = topQuestion
    ? rankedQuestions
        .filter((item) => item.dimension.key === opportunityDimension.key)
        .slice(0, 2)
    : [];
  const opportunityQuestionText = opportunityQuestions.length
    ? opportunityQuestions.map((item) => item.label).join(" and ")
    : responseThemes[opportunityDimension.key];
  const secondOpportunityQuestion = rankedQuestions.find(
    (item) => item.dimension.key !== opportunityDimension.key,
  );

  if (id === "combination") {
    const strengthQuestions = contributingQuestions(strongest.key, latest, {
      favorable: true,
    });
    return {
      text: hasOpportunity
        ? `Within ${opportunityDimension.label.toLowerCase()}, your responses to ${opportunityQuestionText} show the greatest opportunity, while within ${strongest.label.toLowerCase()}, your responses are comparatively favorable.`
        : `No individual assessment question currently stands out as elevated across your five domains, so your combination of characteristics is broadly favorable right now.`,
      questions: [
        ...opportunityQuestions.map((item) =>
          asQuestion(opportunityDimension, item),
        ),
        ...strengthQuestions.map((item) => asQuestion(strongest, item)),
      ],
      primary: opportunityQuestions.map((item) => item.key),
      actionable: hasOpportunity ? opportunityDimension : null,
      alternates: hasOpportunity ? opportunityDimension.actions : null,
      followUps: ["strengths-opportunities", "best-habit"],
    };
  }

  if (id === "strengths-opportunities") {
    return {
      text: !hasOpportunity
        ? "All five domains are within their ideal ranges, and no individual assessment question currently stands out as elevated, so there is no pressing opportunity area right now."
        : `Your greatest current opportunity is within ${opportunityDimension.label.toLowerCase()}, driven most by your response${opportunityQuestions.length > 1 ? "s" : ""} to ${opportunityQuestionText}. ${strongest.label} remains your strongest overall domain${secondOpportunityQuestion ? `, followed by a smaller opportunity within ${secondOpportunityQuestion.dimension.label.toLowerCase()}, driven by your response to ${secondOpportunityQuestion.label}` : ""}.`,
      questions: [
        ...opportunityQuestions.map((item) =>
          asQuestion(opportunityDimension, item),
        ),
        ...(secondOpportunityQuestion
          ? [
              asQuestion(
                secondOpportunityQuestion.dimension,
                secondOpportunityQuestion,
              ),
            ]
          : []),
      ],
      primary: opportunityQuestions.map((item) => item.key),
      actionable: hasOpportunity ? opportunityDimension : null,
      alternates: hasOpportunity ? opportunityDimension.actions : null,
      followUps: ["best-habit", "next-step"],
    };
  }

  if (id === "best-habit") {
    if (!hasOpportunity) {
      return {
        text: `Your scores are within ideal ranges. A small maintenance habit connected to ${strongest.label.toLowerCase()} can help sustain that pattern.`,
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["next-step"],
      };
    }
    return {
      text: `Within ${opportunityDimension.label.toLowerCase()}, your response${opportunityQuestions.length > 1 ? "s" : ""} to ${opportunityQuestionText} ${opportunityQuestions.length > 1 ? "stand" : "stands"} out most, so a habit connected to ${opportunityQuestions[0].label.toLowerCase()} is likely the most useful place to begin. This reflects one option to explore, not a guaranteed outcome.`,
      questions: opportunityQuestions.map((item) =>
        asQuestion(opportunityDimension, item),
      ),
      primary: opportunityQuestions.slice(0, 1).map((item) => item.key),
      actionable: opportunityDimension,
      alternates: opportunityDimension.actions,
      followUps: ["strengths-opportunities", "next-step"],
    };
  }

  if (id.startsWith("link-")) {
    const [firstKey, secondKey] = id.slice("link-".length).split("_");
    const firstDimension = profile.find((item) => item.key === firstKey);
    const secondDimension = profile.find((item) => item.key === secondKey);
    const firstItems =
      firstKey === "CMScore"
        ? rankCopingByFrequency(latest)
        : contributingQuestions(firstKey, latest, { limit: 2 });
    const secondItems =
      secondKey === "CMScore"
        ? rankCopingByFrequency(latest)
        : contributingQuestions(secondKey, latest, { limit: 2 });
    if (!firstItems.length || !secondItems.length) {
      return {
        text: `Your latest assessment does not show elevated responses in both ${firstDimension.label.toLowerCase()} and ${secondDimension.label.toLowerCase()} to compare, so no association can be identified right now.`,
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["profile-summary"],
      };
    }
    return {
      text: `Within your own results, your ${firstDimension.label.toLowerCase()} responses to ${firstItems.map((item) => item.label).join(" and ")} appear alongside your ${secondDimension.label.toLowerCase()} responses to ${secondItems.map((item) => item.label).join(" and ")}. This reflects a pattern within your own assessment, not a population comparison or a causal link.`,
      questions: [
        ...firstItems.map((item) => asQuestion(firstDimension, item)),
        ...secondItems.map((item) => asQuestion(secondDimension, item)),
      ],
      primary: [
        ...firstItems.map((item) => item.key),
        ...secondItems.map((item) => item.key),
      ],
      actionable: null,
      alternates: null,
      followUps: ["best-habit", "strengths-opportunities"],
    };
  }

  if (id.startsWith("domain-")) {
    const dimensionKey = id.slice("domain-".length);
    const dimension = profile.find((item) => item.key === dimensionKey);
    if (dimensionKey === "CMScore") {
      const ranked = rankCopingByFrequency(latest);
      if (!ranked.length) {
        return {
          text: "Your latest assessment does not show a clearly elevated coping response right now.",
          questions: [],
          primary: [],
          actionable: null,
          alternates: null,
          followUps: ["best-habit", "strengths-opportunities"],
        };
      }
      return {
        text: `Based on your latest assessment, your most frequent coping responses are ${ranked.map((item) => item.label).join(", ")}.`,
        questions: ranked.map((item) => ({
          key: item.key,
          label: item.label,
          dimension: "Coping Mechanisms",
          status:
            item.direction === "negative"
              ? "Frequently relied upon"
              : "Frequently practiced",
        })),
        primary: ranked.map((item) => item.key),
        actionable: ranked.some((item) => item.direction === "negative")
          ? dimension
          : null,
        alternates: dimension.actions,
        followUps: ["best-habit", "strengths-opportunities"],
      };
    }
    const elevated = contributingQuestions(dimensionKey, latest, { limit: 3 });
    if (!elevated.length) {
      return {
        text: `Your ${dimension.label.toLowerCase()} responses are within the ideal range, with no individual question currently elevated.`,
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["profile-summary", "next-step"],
      };
    }
    return {
      text: `Within ${dimension.label.toLowerCase()}, your responses to ${elevated.map((item) => item.label).join(", ")} currently show the most room for improvement.`,
      questions: elevated.map((item) => asQuestion(dimension, item)),
      primary: elevated.map((item) => item.key),
      actionable: dimension,
      alternates: dimension.actions,
      followUps: ["best-habit", "next-step"],
    };
  }

  if (id === "similar-people") {
    const insight = comparisonInsights?.["similar-people"];
    const mentalHealth = profile.find(
      (dimension) => dimension.key === "MHScore",
    );
    const physicalHealth = profile.find(
      (dimension) => dimension.key === "PHScore",
    );
    const mentalQuestions = contributingQuestions(mentalHealth.key, latest, {
      limit: 1,
    });
    const physicalQuestions = contributingQuestions(
      physicalHealth.key,
      latest,
      {
        limit: 1,
      },
    );
    return {
      text:
        insight ||
        "Comparison insights are not shown yet. We only display anonymous, associative patterns from people with similar results once enough data is available.",
      questions: [
        ...mentalQuestions.map((item) => asQuestion(mentalHealth, item)),
        ...physicalQuestions.map((item) => asQuestion(physicalHealth, item)),
      ],
      primary: [],
      actionable: null,
      alternates: null,
      followUps: ["best-habit"],
    };
  }

  if (id === "consistent-improvement") {
    const ranked = history
      .filter((entry) => entry.total > 0)
      .sort((a, b) => b.favorable / b.total - a.favorable / a.total);
    if (!ranked.length) {
      return {
        text: "There is not yet enough assessment history to identify consistent patterns. This becomes available once you have at least two assessments a month apart.",
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["what-changed"],
      };
    }
    const top = ranked[0];
    const topDimension = profile.find((dimension) => dimension.key === top.key);
    const topDimensionQuestions = contributingQuestions(
      topDimension.key,
      latest,
    );
    return {
      text: `${topDimension.label} moved favorably in ${top.favorable} of ${top.total} comparisons across your assessment history, making it the most consistently improving area${topDimensionQuestions.length ? `. Your responses to ${topDimensionQuestions.map((item) => item.label).join(" and ")} remain the largest current contributors within it` : ""}. This is an association observed in your own results, not a guaranteed cause.`,
      questions: topDimensionQuestions.map((item) =>
        asQuestion(topDimension, item),
      ),
      primary: topDimensionQuestions.map((item) => item.key),
      actionable: topDimensionQuestions.length ? topDimension : null,
      alternates: topDimensionQuestions.length ? topDimension.actions : null,
      followUps: ["what-changed", "next-step"],
    };
  }

  if (id === "what-changed") {
    if (!previous) {
      return {
        text: "A comparison will be available after you complete another assessment at least one month from your latest assessment.",
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["profile-summary"],
      };
    }
    const changed = profile.filter((dimension) => dimension.change !== 0);
    if (!changed.length) {
      return {
        text: `Your scores were unchanged between ${formatAssessmentDate(previous)} and ${formatAssessmentDate(latest)} across all five health dimensions.`,
        questions: [],
        primary: [],
        actionable: null,
        alternates: null,
        followUps: ["consistent-improvement"],
      };
    }
    const sorted = [...changed].sort(
      (first, second) => Math.abs(second.change) - Math.abs(first.change),
    );
    const mostChanged = sorted[0];
    const changes = sorted
      .map(
        (dimension) =>
          `${dimension.label} moved from ${dimension.previousScore} to ${dimension.score} (${dimension.change < 0 ? "more favorable" : "less favorable"})`,
      )
      .join("; ");
    const movedQuestions = (questionCatalog[mostChanged.key] || [])
      .map((item) => ({
        ...item,
        previousValue: Number(previous?.[item.key]) || 0,
        value: Number(latest?.[item.key]) || 0,
      }))
      .filter((item) => item.value !== item.previousValue)
      .sort(
        (first, second) =>
          Math.abs(second.value - second.previousValue) -
          Math.abs(first.value - first.previousValue),
      )
      .slice(0, 3);
    const worsened = mostChanged.change > 0 && movedQuestions.length > 0;
    return {
      text: `Comparing your pre-assessment from ${formatAssessmentDate(previous)} with your post-assessment from ${formatAssessmentDate(latest)}, ${changes}. Lower scores are more favorable.${movedQuestions.length ? ` Within ${mostChanged.label.toLowerCase()}, your responses to ${movedQuestions.map((item) => item.label).join(", ")} changed the most.` : ""}`,
      questions: movedQuestions.map((item) => ({
        key: item.key,
        label: item.label,
        dimension: mostChanged.label,
        status:
          item.value > item.previousValue
            ? "Moved less favorable"
            : "Moved more favorable",
      })),
      primary: movedQuestions.map((item) => item.key),
      actionable: worsened ? mostChanged : null,
      alternates: worsened ? mostChanged.actions : null,
      followUps: ["consistent-improvement", "next-step"],
    };
  }

  if (id === "profile-summary") {
    return {
      text: !hasOpportunity
        ? "Your profile is within ideal ranges across all five dimensions, and no individual assessment question currently stands out as elevated. Maintaining current routines is the focus, with small improvements as they fit your life."
        : `Your profile shows both strengths and opportunities: ${strongest.label} is comparatively strongest, while your greatest current opportunity is within ${opportunityDimension.label.toLowerCase()}, driven most by your response${opportunityQuestions.length > 1 ? "s" : ""} to ${opportunityQuestionText}.`,
      questions: opportunityQuestions.map((item) =>
        asQuestion(opportunityDimension, item),
      ),
      primary: opportunityQuestions.map((item) => item.key),
      actionable: hasOpportunity ? opportunityDimension : null,
      alternates: hasOpportunity ? opportunityDimension.actions : null,
      followUps: ["combination", "next-step"],
    };
  }

  // next-step
  if (!hasOpportunity) {
    return {
      text: `Your scores are within ideal ranges. Maintaining the routines that support ${strongest.label.toLowerCase()} and your other domains is likely most beneficial right now.`,
      questions: [],
      primary: [],
      actionable: null,
      alternates: null,
      followUps: ["profile-summary"],
    };
  }
  return {
    text: `Because your response${opportunityQuestions.length > 1 ? "s" : ""} to ${opportunityQuestionText} within ${opportunityDimension.label.toLowerCase()} ${opportunityQuestions.length > 1 ? "are" : "is"} currently elevated, strengthening that routine is likely the most beneficial next step.`,
    questions: opportunityQuestions.map((item) =>
      asQuestion(opportunityDimension, item),
    ),
    primary: opportunityQuestions.slice(0, 1).map((item) => item.key),
    actionable: opportunityDimension,
    alternates: opportunityDimension.actions,
    followUps: ["best-habit", "strengths-opportunities"],
  };
}

export default function ExploreHealth({ onAddToPlan, onTrackHabit }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDimension, setSelectedDimension] = useState("MHScore");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [altIndex, setAltIndex] = useState(0);
  const [showVariables, setShowVariables] = useState(false);
  const [saved, setSaved] = useState(false);
  const [nextStepSaved, setNextStepSaved] = useState(false);
  const [nextStepAltIndex, setNextStepAltIndex] = useState(0);
  const [showImportantNote, setShowImportantNote] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    profile: false,
    domains: false,
    questions: false,
  });

  useEffect(() => {
    async function loadAssessments() {
      try {
        const [user, attributes] = await Promise.all([
          getCurrentUser(),
          fetchUserAttributes().catch(() => ({})),
        ]);
        const userIds = [
          user.userId,
          user.username,
          user.userId,
          attributes.sub,
          attributes.email,
        ].filter((value) => typeof value === "string" && value.trim());
        const uniqueUserIds = [...new Set(userIds)];
        const userRecords = [];
        let nextToken = null;

        do {
          const response = await client.graphql({
            query: listAssessmentScores,
            variables: {
              filter: {
                or: uniqueUserIds.flatMap((userId) => [
                  { userId: { eq: userId } },
                  { owner: { eq: userId } },
                ]),
              },
              limit: 100,
              nextToken,
            },
          });
          const scores = response?.data?.listAssessmentScores;
          userRecords.push(...(scores?.items || []));
          nextToken = scores?.nextToken || null;
        } while (nextToken);

        setRecords(
          [
            ...new Map(
              userRecords.filter(Boolean).map((record) => [record.id, record]),
            ).values(),
          ].sort(
            (first, second) =>
              assessmentDateFor(second) - assessmentDateFor(first),
          ),
        );
      } catch (loadError) {
        console.error("Unable to load assessment exploration data:", loadError);
        setError("Your assessment data could not be loaded right now.");
      } finally {
        setLoading(false);
      }
    }
    loadAssessments();
  }, []);

  const latest = records[0];
  const previous = records
    .slice(1)
    .find((record) => isAtLeastOneMonthEarlier(latest, record));
  const profile = useMemo(
    () =>
      dimensions.map((dimension) => {
        const score = scoreFor(latest, dimension);
        const previousScore = scoreFor(previous, dimension);
        return {
          ...dimension,
          score,
          previousScore,
          ratio: score / dimension.max,
          change: previous ? score - previousScore : 0,
        };
      }),
    [latest, previous],
  );
  const opportunity = [...profile].sort((a, b) => b.ratio - a.ratio)[0];
  const strength = [...profile].sort((a, b) => a.ratio - b.ratio)[0];
  const selected =
    profile.find((dimension) => dimension.key === selectedDimension) ||
    profile[0];
  // Placeholder for a future anonymized-cohort backend; kept empty until real data is wired in.
  const comparisonInsights = {};
  const allResultsIdeal = profile.every(
    (dimension) => dimension.score <= dimension.idealMax,
  );
  const history = useMemo(() => {
    const sorted = [...records].sort(
      (first, second) => assessmentDateFor(first) - assessmentDateFor(second),
    );
    return dimensions.map((dimension) => {
      const points = sorted.map((record) => ({
        date: assessmentDateFor(record),
        score: scoreFor(record, dimension),
      }));
      let favorable = 0;
      let total = 0;
      for (let index = 1; index < points.length; index += 1) {
        if (!isAtLeastOneMonthApart(points[index].date, points[index - 1].date))
          continue;
        total += 1;
        if (points[index].score < points[index - 1].score) favorable += 1;
      }
      return { key: dimension.key, favorable, total };
    });
  }, [records]);
  const toggleSection = (section) =>
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));

  const ask = (input = question) => {
    const prompt = typeof input === "object" ? input.text : input;
    if (!prompt || !prompt.trim() || !latest) return;
    const id = typeof input === "object" ? input.id : classifyQuestion(prompt);
    setQuestion(prompt);
    setAltIndex(0);
    setShowVariables(false);
    setSaved(false);
    setAnswer(
      buildInsight(id, {
        profile,
        previous,
        latest,
        history,
        comparisonInsights,
      }),
    );
    window.setTimeout(
      () =>
        document
          .getElementById("whe-answer")
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      0,
    );
  };

  if (loading)
    return (
      <div className="whe-loading">
        Preparing your Wholistic Health exploration...
      </div>
    );
  if (error) return <div className="whe-loading">{error}</div>;
  if (!latest)
    return (
      <div className="whe-loading">
        Complete an assessment to unlock your personal Wholistic Health
        exploration.
      </div>
    );

  return (
    <div className="whe-shell">
      <section
        className="whe-profile-grid"
        aria-label="Wholistic Health Profile"
      >
        <div className="whe-profile-summary">
          <button
            className="whe-section-toggle"
            type="button"
            aria-expanded={expandedSections.profile}
            aria-controls="whe-profile-content"
            onClick={() => toggleSection("profile")}
          >
            <span className="whe-kicker whe-primary-heading">
              <span className="whe-section-number">1</span>Your Whole Health
              Profile
            </span>
            <span className="whe-toggle-icon" aria-hidden="true">
              {expandedSections.profile ? "-" : "+"}
            </span>
          </button>
          {expandedSections.profile && (
            <div className="whe-section-content" id="whe-profile-content">
              {showImportantNote && (
                <div className="whe-important-note">
                  <p>
                    <strong>Important:</strong> Interpretations on this page
                    identify patterns to explore. It does not establish cause,
                    predict an outcome, or replace professional care.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowImportantNote(false)}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <p className="whe-profile-highlight">
                {allResultsIdeal
                  ? "Your scores are within ideal ranges"
                  : `${strength.label} is a current strength + ${opportunity.label} has room to improve`}
              </p>
              <p>
                {allResultsIdeal
                  ? "Continue the routines that support your health and use your results to guide small maintenance choices."
                  : `Your results suggest that ${strength.label.toLowerCase()} is currently one of your comparatively stronger areas, while ${opportunity.label.toLowerCase()} may be a good place to focus first.`}
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="whe-section whe-domain-section">
        <div className="whe-profile-dimensions">
          <div className="whe-section-heading">
            <div>
              <button
                className="whe-section-toggle"
                type="button"
                aria-expanded={expandedSections.domains}
                aria-controls="whe-domains-content"
                onClick={() => toggleSection("domains")}
              >
                <span className="whe-kicker whe-primary-heading">
                  <span className="whe-section-number">2</span>Explore Each
                  Dimension of Health
                </span>
                <span className="whe-toggle-icon" aria-hidden="true">
                  {expandedSections.domains ? "-" : "+"}
                </span>
              </button>
            </div>
          </div>
          {expandedSections.domains && (
            <div className="whe-section-content" id="whe-domains-content">
              <p className="whe-section-description">
                Select a dimension for its score, contributing responses, and
                change over time. Lower assessment scores indicate more ideal
                results.
              </p>
              <div className="whe-dimension-cards">
                {profile.map((dimension) => (
                  <button
                    key={dimension.key}
                    className={`whe-dimension-card ${selectedDimension === dimension.key ? "is-selected" : ""}`}
                    style={{ "--dimension-color": dimension.color }}
                    onClick={() => setSelectedDimension(dimension.key)}
                  >
                    <span>{dimension.label}</span>
                    <small>Your Score</small>
                    <strong>{dimension.score}</strong>
                    {previous && (
                      <em>
                        {dimension.change === 0
                          ? "No change"
                          : `${dimension.change < 0 ? "More favorable" : "Less favorable"} by ${Math.abs(dimension.change)}`}
                      </em>
                    )}
                  </button>
                ))}
              </div>
              <aside
                className="whe-domain-summary"
                style={{ "--dimension-color": selected.color }}
                aria-live="polite"
              >
                <p className="whe-kicker">Selected domain</p>
                <h4>
                  {selected.label}:{" "}
                  {titleCase(levelFor(selected.score, selected))}
                </h4>
                <p>{domainSummary(selected, previous)}</p>
              </aside>
            </div>
          )}
        </div>
      </section>

      <section className="whe-ask-section whe-results-section">
        <button
          className="whe-section-toggle"
          type="button"
          aria-expanded={expandedSections.questions}
          aria-controls="whe-questions-content"
          onClick={() => toggleSection("questions")}
        >
          <span className="whe-kicker whe-primary-heading">
            <span className="whe-section-number">3</span>Ask Questions About
            Your Results
          </span>
          <span className="whe-toggle-icon" aria-hidden="true">
            {expandedSections.questions ? "-" : "+"}
          </span>
        </button>
        {expandedSections.questions && (
          <div className="whe-section-content" id="whe-questions-content">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                ask();
              }}
            >
              <input
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="Ask a question about your assessment"
                aria-label="Ask about my results"
              />
              <button type="submit">Ask</button>
            </form>
            <div className="whe-question-list">
              {questionPrompts.map((prompt) => (
                <button key={prompt.id} onClick={() => ask(prompt)}>
                  {prompt.text}
                </button>
              ))}
            </div>
            {answer && (
              <article className="whe-answer" id="whe-answer">
                <p className="whe-kicker">Answer Based on Your Results</p>
                <p>{answer.text}</p>

                {answer.questions.length > 0 && (
                  <div className="whe-answer-variables">
                    <button
                      type="button"
                      className="whe-text-button"
                      onClick={() => setShowVariables((current) => !current)}
                    >
                      {showVariables ? "Hide" : "See"} the assessment questions
                      behind this insight
                    </button>
                    {showVariables && (
                      <ul>
                        {answer.questions.map((item) => (
                          <li
                            key={item.key}
                            className={
                              answer.primary?.includes(item.key)
                                ? "is-primary"
                                : ""
                            }
                          >
                            <strong>{item.label}</strong>
                            <span>{item.dimension}</span>
                            <em>{item.status}</em>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {answer.alternates && answer.alternates.length > 0 && (
                  <div className="whe-answer-alternate">
                    <p>
                      <strong>Suggestion:</strong>{" "}
                      {
                        answer.alternates[altIndex % answer.alternates.length]
                          .text
                      }
                    </p>
                    <p className="whe-suggestion-why">
                      <strong>Why:</strong>{" "}
                      {
                        answer.alternates[altIndex % answer.alternates.length]
                          .why
                      }
                    </p>
                    {answer.alternates.length > 1 && (
                      <button
                        type="button"
                        className="whe-text-button"
                        onClick={() =>
                          setAltIndex(
                            (current) =>
                              (current + 1) % answer.alternates.length,
                          )
                        }
                      >
                        Explore an alternative recommendation
                      </button>
                    )}
                  </div>
                )}

                {answer.actionable && (
                  <div className="whe-answer-actions">
                    <button
                      onClick={() => {
                        setSaved(true);
                        onAddToPlan?.(
                          answer.alternates
                            ? answer.alternates[
                                altIndex % answer.alternates.length
                              ].text
                            : answer.actionable.actions[0].text,
                        );
                      }}
                    >
                      Add to My Health Plan
                    </button>
                    <button
                      className="whe-secondary-button"
                      onClick={() =>
                        onTrackHabit?.(
                          answer.alternates
                            ? answer.alternates[
                                altIndex % answer.alternates.length
                              ].text
                            : answer.actionable.actions[0].text,
                        )
                      }
                    >
                      Track This Habit
                    </button>
                    {saved && <span>Insight saved for this session.</span>}
                  </div>
                )}

                {answer.followUps && answer.followUps.length > 0 && (
                  <div className="whe-answer-followups">
                    <p className="whe-kicker">Explore next</p>
                    <div className="whe-question-list">
                      {answer.followUps.map((followId) => {
                        const followPrompt = questionPrompts.find(
                          (item) => item.id === followId,
                        );
                        return followPrompt ? (
                          <button
                            key={followId}
                            onClick={() => ask(followPrompt)}
                          >
                            {followPrompt.text}
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </article>
            )}
          </div>
        )}
      </section>

      <section className="whe-next-step">
        <div>
          <p className="whe-kicker">Your Next Best Step</p>
          <h3>
            {allResultsIdeal
              ? "Maintain the routines that support your health."
              : `Strengthen one ${opportunity.label.toLowerCase()} routine.`}
          </h3>
          <p>
            <strong>Why this?</strong>{" "}
            {allResultsIdeal
              ? "Your scores are within ideal ranges, so maintaining helpful routines is the focus."
              : `${opportunity.label} is one of your larger opportunities, so a small, repeatable action may be more useful than trying to change everything at once.`}
          </p>
          <p>
            <strong>Try this:</strong>{" "}
            {
              opportunity.actions[nextStepAltIndex % opportunity.actions.length]
                .text
            }
          </p>
          <p>
            <strong>Why:</strong>{" "}
            {
              opportunity.actions[nextStepAltIndex % opportunity.actions.length]
                .why
            }
          </p>
          {!allResultsIdeal && opportunity.actions.length > 1 && (
            <button
              type="button"
              className="whe-text-button"
              onClick={() =>
                setNextStepAltIndex(
                  (current) => (current + 1) % opportunity.actions.length,
                )
              }
            >
              Explore an alternative recommendation
            </button>
          )}
          <p>
            <strong>Reassess:</strong> Complete another assessment after you
            have had time to practice the habit.
          </p>
        </div>
        {!allResultsIdeal && (
          <div className="whe-next-actions">
            <button
              onClick={() => {
                setNextStepSaved(true);
                onAddToPlan?.(
                  opportunity.actions[
                    nextStepAltIndex % opportunity.actions.length
                  ].text,
                );
              }}
            >
              Add to My Health Plan
            </button>
            <button
              className="whe-secondary-button"
              onClick={() =>
                onTrackHabit?.(
                  opportunity.actions[
                    nextStepAltIndex % opportunity.actions.length
                  ].text,
                )
              }
            >
              Track This Habit
            </button>
            {nextStepSaved && <span>Insight saved for this session.</span>}
          </div>
        )}
      </section>
      <footer className="whe-footer">
        Your health is not one score. Explore the connections.
      </footer>
    </div>
  );
}
